/**
 * MyMilestonesSection — section permanente « Mes paliers » du cockpit.
 * Liste tous les paliers déjà franchis (all-time) avec badge doré et
 * bouton Partager qui ouvre MilestoneShareDialog (story / carré).
 */
import React, { useEffect, useState } from 'react';
import { Trophy, Star, FileCheck, ShieldCheck, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../lib/api';
import MilestoneShareDialog from './MilestoneShareDialog';

const ICONS = {
    star: Star,
    'file-check': FileCheck,
    'shield-check': ShieldCheck,
    'share-2': Share2,
};

const VISIBLE_COUNT = 6;

export default function MyMilestonesSection() {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get('/cockpit/milestones/achieved')
            .then((r) => setData(r.data))
            .catch(() => {});
    }, []);

    if (!data || !data.milestones || data.milestones.length === 0) return null;

    const milestones = expanded ? data.milestones : data.milestones.slice(0, VISIBLE_COUNT);
    const hasMore = data.milestones.length > VISIBLE_COUNT;

    return (
        <section
            data-testid="my-milestones-section"
            className="rounded-2xl p-4 sm:p-5 border mt-4"
            style={{
                background: 'linear-gradient(135deg, rgba(245,179,1,0.08), rgba(104,23,147,0.10))',
                borderColor: 'rgba(245,179,1,0.30)',
            }}
        >
            <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{
                         background: 'linear-gradient(135deg, #F5B301, #FF914D)',
                         color: '#fff',
                         boxShadow: '0 4px 12px rgba(245,179,1,0.35)',
                     }}>
                    <Trophy className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#F5B301]">
                        Mes paliers
                    </p>
                    <p className="text-sm text-foreground mt-0.5">
                        {data.total} palier{data.total > 1 ? 's' : ''} franchi{data.total > 1 ? 's' : ''} — partagez-les en story ou en photo.
                    </p>
                </div>
            </div>

            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="my-milestones-list">
                {milestones.map((m) => {
                    const Icon = ICONS[m.icon] || Trophy;
                    return (
                        <li key={m.code}
                            data-testid={`my-milestone-${m.code}`}
                            className="flex items-center gap-2.5 rounded-xl p-2.5 border backdrop-blur-sm"
                            style={{
                                background: 'rgba(255,255,255,0.04)',
                                borderColor: 'rgba(245,179,1,0.30)',
                            }}>
                            <span className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                  style={{
                                      background: 'linear-gradient(135deg, #F5B301, #FF914D)',
                                      color: '#fff',
                                  }}>
                                <Icon className="w-4 h-4" strokeWidth={2.5} />
                            </span>
                            <span className="text-sm font-semibold text-foreground leading-tight flex-1 min-w-0 truncate">
                                {m.label}
                            </span>
                            <button
                                type="button"
                                onClick={() => setSelected(m)}
                                aria-label={`Partager le palier ${m.label}`}
                                data-testid={`my-milestone-share-${m.code}`}
                                className="shrink-0 p-2 rounded-lg text-[#F5B301] hover:bg-[#F5B301]/15 transition"
                            >
                                <Share2 className="w-4 h-4" />
                            </button>
                        </li>
                    );
                })}
            </ul>

            {hasMore && (
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    data-testid="my-milestones-toggle"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#F5B301] hover:underline"
                >
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {expanded ? 'Réduire' : `Voir les ${data.milestones.length - VISIBLE_COUNT} autres paliers`}
                </button>
            )}

            <MilestoneShareDialog
                milestone={selected}
                open={!!selected}
                onClose={() => setSelected(null)}
            />
        </section>
    );
}
